import { Button, Input, Markdown, Typography } from '@mgmresorts/wcl-lab';
import { navigate } from 'hookrouter';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { KnownError } from '../../../api/errors';
import { mlifeVerificationCodeRequest } from '../../../api/fetch/mlife-activation';
import { Alert } from '../../../components/ui/Alert';
import { Container } from '../../../components/ui/Container';
import { Link } from '../../../components/ui/Link';
import { Logo } from '../../../components/ui/Logo';
import { PageLayout } from '../../../components/ui/PageLayout';
import { RadioCheck } from '../../../components/ui/icons/RadioCheck';
import { useContent } from '../../../hooks/useContent';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { useInput } from '../../../hooks/useInput';
import { captureEvent, captureException } from '../../../monitoring/logrocket';
import { useEvent } from '../../../utils/analytics/useEvent';
import { activationCodeValidator } from '../../../utils/validators';
import { StyledContent } from '../MlifeActivation.styled';
import { MlifeActivationContext } from '../MlifeActivationContext';

export function MlifeVerification() {
  const trackEvent = useEvent();
  const { activationRequest, resetActivationForm, setActivationRequest, setStep } = useContext(MlifeActivationContext);
  const { validateField, invalidFields } = useFieldValidation([activationCodeValidator]);
  const { bind: bindActivationCode, value: activationCode } = useInput('', 'activationCode', undefined, validateField);
  const { getContentByPath } = useContent();
  const [trackedError, setTrackedError] = useState('');
  const [error, setErrorState] = useState<null | string>(null);
  const [verificationState, setVerificationState] = useState<'init' | 'activating' | 'success' | 'error'>('init');

  useEffect(() => {
    if (!activationRequest) {
      navigate('activate');
    }
  }, [activationRequest]);

  const errorMessage = useMemo<string>(
    () =>
      invalidFields.includes('activationCode')
        ? !activationCode || activationCode.length === 0
          ? getContentByPath(`mlifeActivation.fields.activationCode.required`)
          : getContentByPath(`mlifeActivation.fields.activationCode.error`)
        : '',
    [activationCode, getContentByPath, invalidFields],
  );

  useEffect(() => {
    if (errorMessage !== trackedError) {
      trackEvent(
        'identity – verification email code - failure',
        'verification email input error',
        'verifyemailinputerror',
        {
          errorDisplayed: errorMessage,
        },
      );
      setTrackedError(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, trackEvent, setTrackedError]);

  const requestMlifeActivation = useCallback(async () => {
    try {
      if (!activationRequest) {
        return null;
      }

      setVerificationState('activating');

      captureEvent('Activation: Verification', {
        email: String(activationRequest.login),
      });

      const guestInfo = await mlifeVerificationCodeRequest({
        activationCode,
        activationKid: activationRequest.activationKid,
        login: activationRequest.login,
      });

      setVerificationState('success');

      setActivationRequest({
        ...activationRequest,
        ...guestInfo,
      });

      await new Promise(resolve => {
        setTimeout(() => {
          resolve(true);
        }, 3000);
      });

      setStep('accountSetup');
      trackEvent('identity – verification email code - success', 'valid verification code', 'verifyemailsuccess');
    } catch (error) {
      setVerificationState('error');

      let message: string;
      if (error instanceof KnownError) {
        message = error.message;
      } else {
        message = getContentByPath('mlifeActivation.verifySection.error');
      }

      setErrorState(message);

      trackEvent('identity – verification email code - failure', 'invalid verification code', 'verifyemailfail', {
        errorDisplayed: message,
      });
      captureException(error as Error, 'activate', {
        username: String(activationRequest?.login),
        step: 'activate-request',
      });
    }
  }, [activationCode, activationRequest, getContentByPath, setActivationRequest, setStep, trackEvent]);

  if (verificationState === 'success') {
    return (
      <StyledContent>
        <PageLayout
          title={getContentByPath('mlifeActivation.verifySection.title')}
          description={getContentByPath('mlifeActivation.description')}
        >
          <Container height='auto'>
            <div className='identity__email-verified-success'>
              <RadioCheck fill='#00012b' />
              <Typography variant='paragraph' element='p' className='identity__email-verified-success-info'>
                {getContentByPath('mlifeActivation.verifySection.successInfo')}
              </Typography>
            </div>
          </Container>
        </PageLayout>
      </StyledContent>
    );
  }

  return (
    <StyledContent>
      <PageLayout
        title={getContentByPath('mlifeActivation.verifySection.title')}
        description={getContentByPath('mlifeActivation.description')}
      >
        <Container height='auto'>
          <Logo className='identity__page-logo' />

          <Typography variant='h1' className='identity__page-heading'>
            {getContentByPath('mlifeActivation.verifySection.title')}
          </Typography>

          {verificationState === 'error' && error && <Alert text={error} />}

          <Markdown paragraphProps={{ variant: 'paragraph' }} className='identity__verification-description'>
            {(getContentByPath('mlifeActivation.verifySection.body') as string).replace(
              '%email%',
              activationRequest?.login ?? '',
            )}
          </Markdown>

          <Input
            label={getContentByPath('mlifeActivation.fields.activationCode.title')}
            id='activationCode'
            testId='activationCode'
            disabled={verificationState === 'activating'}
            aria-label={getContentByPath('mlifeActivation.fields.activationCode.label')}
            className={`identity__field identity__field-activation-code ${
              invalidFields.includes('activationCode') ? 'with-error' : ''
            }`}
            error={!!errorMessage}
            hint={errorMessage}
            {...bindActivationCode}
          />

          {activationCode.length === 0 && (
            <Alert type='info' text={getContentByPath('mlifeActivation.verifySection.message')}>
              <Typography variant='paragraph' element='p' margin='0'>
                <Link onClick={resetActivationForm}>{getContentByPath('mlifeActivation.verifySection.resend')}</Link>
              </Typography>
            </Alert>
          )}

          <Button
            sticky
            testId='activate-button'
            ariaLabel={getContentByPath('mlifeActivation.verifySection.label')}
            onClick={requestMlifeActivation}
            className='form-button identity__form-submit'
            disabled={!activationCode || invalidFields.includes('activationCode')}
            loading={verificationState === 'activating'}
          >
            <Typography>{getContentByPath('mlifeActivation.verifySection.cta')}</Typography>
          </Button>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}
